import { graphql, useStaticQuery } from 'gatsby'
import reactStringReplace from 'react-string-replace'
import React, { useState } from 'react'
import pluralizeWord from 'pluralize'
import Tooltip from '../Tooltip'
import { CallToAction } from 'components/CallToAction'
import { Close } from 'components/Icons'

export interface TooltipContentProps {
    title: string
    slug: string
    description: string
    video?: string
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export interface MdxNode {
    frontmatter: {
        title: string
        featuredVideo: string
    }
    fields: {
        slug: string
    }
    excerpt: string
}

export const formatNode = (node: MdxNode) => ({
    title: node?.frontmatter?.title,
    slug: node?.fields?.slug,
    description: node?.excerpt,
    video: node?.frontmatter?.featuredVideo,
})

export const TooltipContent = ({ slug, description, title, video, setOpen }: TooltipContentProps) => {
    const [view, setView] = useState('article')
    return (
        title &&
        slug &&
        description && (
            <div className="relative w-[320px] p-4">
                <button onClick={() => setOpen(false)} className="absolute top-2 right-1">
                    <Close opacity="100" className="h-3 w-3 opacity-40 transition-opacity hover:opacity-60" />
                </button>
                {video && (
                    <div className="relative mb-4 grid grid-cols-2 pb-4">
                        <button onClick={() => setView('article')} className="font-semibold">
                            Article
                        </button>
                        <button onClick={() => setView('video')} className="font-semibold">
                            Video
                        </button>
                        <span
                            className={`absolute bottom-0 h-[2px] w-1/2 rounded-full bg-red transition-all ${
                                view === 'article' ? 'translate-x-0' : 'translate-x-full'
                            }`}
                        />
                    </div>
                )}
                {view === 'article' ? (
                    <>
                        <h4 className="m-0 text-2xl">{title}</h4>
                        <p className="m-0 mt-4 text-base">{description}</p>
                        {slug && (
                            <CallToAction size="sm" className="mt-6" width="full" to={slug}>
                                Continue reading
                            </CallToAction>
                        )}
                    </>
                ) : (
                    <iframe className="m-0 aspect-video w-full" src={video} />
                )}
            </div>
        )
    )
}

const escapeRegex = (str) => {
    // eslint-disable-next-line
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
}

export default function GlossaryElement({ as = 'p', ...other }) {
    const Element = as
    let children = other.children
    if (typeof other.children === 'string') {
        const regex = new RegExp(
            `(${glossary?.nodes
                .map(({ word, pluralize }) => {
                    const wordPlural = pluralize && pluralizeWord(word)
                    return `\\b${
                        wordPlural && wordPlural !== word ? `${escapeRegex(wordPlural)}\\b|` : ''
                    }${escapeRegex(word)}`
                })
                .join('\\b|')}\\b)`,
            'gi'
        )
        children = reactStringReplace(other.children, regex, (match, i) => {
            const glossaryItem = glossary?.nodes.find(
                ({ word, pluralize }) =>
                    word.toLowerCase() === match.toLowerCase() ||
                    (pluralize && word.toLowerCase() === pluralizeWord.singular(word).toLowerCase())
            )
            return (
                <Tooltip
                    className="border-b border-dashed border-gray-accent-light dark:border-gray-accent-dark"
                    key={match + i}
                    content={() => (
                        <TooltipContent
                            title={
                                glossaryItem?.page?.frontmatter?.title ||
                                glossaryItem?.word.charAt(0).toUpperCase() + glossaryItem?.word.slice(1)
                            }
                            description={glossaryItem?.description || glossaryItem?.page?.excerpt}
                            slug={glossaryItem?.slug}
                        />
                    )}
                >
                    <span className="cursor-default">{match}</span>
                </Tooltip>
            )
        })
    }
    return <Element>{children}</Element>
}
